/**
 * Every word the Frame picker says, in one table — FRAME_PICKER_DESIGN.md §6.2 `copy.ts`.
 *
 * ONE table, on the `studio/views/studio.copy.ts` precedent (which is itself on the
 * `pdf-editor/views/notices.copy.ts` one): a sentence written at its call site drifts from the
 * one in the design, and a QA row cannot address a string that has no name. Every row is a
 * constant or a FUNCTION of the numbers it mentions — never a template a caller fills in.
 *
 * Voice (§6.2, §6.6): the mocks' words, sentence case, no "playhead", "scrub", "proxy". The
 * §3.5 refusal strings are mirrored here for the CLIENT-SIDE check (§4.3: the browser mirrors
 * the two user-visible numbers; the edge is authoritative, D-3). The numbers come from
 * `FRAME_LIMITS`, never from a literal, so the design, the constants and this table move together.
 *
 * This module imports nothing from React and nothing from another picker module except the
 * limits, so every other module can import it without a cycle.
 */
import { FRAMEPICKER_TTL_HOURS, FRAME_LIMITS } from './state/picker-state';
import { formatHms } from './media/timeline';
import { copyOf, fmtCopy } from '../../constants/copy-deck.prod-fixes';

/** m:ss — the summary chip and the frames grid ("0:04–0:48"). Floored, never rounded. */
export function clock(t: number): string {
    const total = Math.max(0, Math.floor(t));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * R14 (slice L, BUG-022, R14-SRV5 naming grammar): the clock as the SERVER writes it into a file name -- `<minutes>_<ss>.<mmm>`,
 * minutes unbounded, seconds two digits, three decimals rounded to the millisecond, `_` where the picker's counter has `:`
 * (not a file-name character): `clockName(1.4667) === '0_01.467'`, `clockName(65.5) === '1_05.500'`, `clockName(7500) === '125_00.000'`.
 * Mirrors the shared lib's `FramePickerNaming.formatClock`.
 */
export function clockName(t: number): string {
    const ms = Math.round(Math.max(0, Number.isFinite(t) ? t : 0) * 1000);
    const m = Math.floor(ms / 60_000);
    const s = Math.floor((ms % 60_000) / 1000);
    return `${m}_${String(s).padStart(2, '0')}.${String(ms % 1000).padStart(3, '0')}`;
}

/**
 * h:mm:ss.mmm — the sheet's per-cell stamp, the same shape ffmpeg's `%{pts\:hms}` prints (§4.2),
 * so the preview and the real sheet read alike. Mirrors the Appendix `formatHms` signature; the
 * caller may hand in W2's own function instead.
 */
export function clockHms(t: number): string {
    const safe = Math.max(0, t);
    const whole = Math.floor(safe);
    const ms = Math.round((safe - whole) * 1000);
    const h = Math.floor(whole / 3600);
    const m = Math.floor((whole % 3600) / 60);
    const s = whole % 60;
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

/** Seconds with up to three decimals and no trailing zeros ("1.567", "2", "0.5"). */
export function seconds(t: number): string {
    return String(Math.round(Math.max(0, t) * 1000) / 1000);
}

/** Whole megabytes for the estimate rows ("about 12 MB"); "<1 MB" under one. */
export function megabytes(bytes: number): string {
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? '<1 MB' : `${Math.round(mb)} MB`;
}

function plural(n: number, one: string, many: string): string {
    return n === 1 ? one : many;
}

/** R14U-11: a clip this coarse (or coarser) says its intervals in frames, where a frame is a moment a user can count. */
export const COARSE_CLIP_MAX_FPS = 5;

/**
 * The interval as a whole number of frames, or null. On ANY known rate an interval at or below one frame (within 1e-6 above it too,
 * so `1/30` read back with float noise still counts) is 1 -- "every frame" (R14Q-27: a restored 1/60 s on a 30 fps clip delivers
 * one sample per frame, and the summary must say so). Whole multiples
 * ("every 2nd frame") are read on a COARSE clip only (`fps` ≤ `COARSE_CLIP_MAX_FPS`, `intervalS × fps` within 1e-6 of an integer).
 * `intervalInFrames(0.5, 2) === 1`, `(1, 2) === 2`, `(1 / 60, 30) === 1`, `(0.5, 30) === null` (fine clip, 15 frames),
 * `(0.75, 2) === null` (a frame and a half), `(0.5, undefined) === null`.
 */
export function intervalInFrames(intervalS: number, fps?: number | null): number | null {
    if (typeof fps !== 'number' || !Number.isFinite(fps) || fps <= 0) return null;
    if (intervalS > 0 && intervalS <= 1 / fps + 1e-6) return 1;
    if (fps > COARSE_CLIP_MAX_FPS) return null;
    const n = intervalS * fps;
    const k = Math.round(n);
    return k >= 1 && Math.abs(n - k) <= 1e-6 ? k : null;
}

/** "1st", "2nd", "3rd", "4th" … "11th", "12th", "13th", "21st", "22nd", "23rd", "101st". */
export function ordinal(n: number): string {
    const k = Math.max(0, Math.floor(Number.isFinite(n) ? n : 0));
    const mod100 = k % 100;
    const suffix = mod100 >= 11 && mod100 <= 13 ? 'th' : k % 10 === 1 ? 'st' : k % 10 === 2 ? 'nd' : k % 10 === 3 ? 'rd' : 'th';
    return `${k}${suffix}`;
}

/** "MP4", "MP4 or M4V", "MP4, M4V or MOV". */
function orList(items: readonly string[]): string {
    if (items.length <= 1) return items[0] ?? '';
    return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/** R14 (slice H, R14U-06 / R14Q-06): what the page's files are -- the noun every Files-pane sentence takes. */
export type SourceNoun = 'video' | 'GIF' | 'image';

/**
 * Round 4 (R4-P1): the three modes as the user sees them. The tree's own names ("Many frames",
 * "Frame grid") and paragraphs stay its own; the panel shows these instead so a first-time user
 * reads what they will GET. Keyed by the tree's `propertyValue`.
 */
const MODE_WORDS: Readonly<Record<string, { label: string; hint: string }>> = Object.freeze({
    SCREENSHOT_SPECIFIC_FRAME: { label: 'One frame', hint: 'The frame you\u2019re on, as one image.' },
    SCREENSHOT_MULTIPLE_FRAMES: { label: 'Several frames', hint: 'A set of frames, zipped together.' },
    // Review 7 P2-04: a sheet tiles the frames you ask for (12 by default), not "all the frames".
    CONTACT_SHEET: { label: 'Contact sheet', hint: 'Chosen frames, tiled in one image.' },
});

/**
 * R14 (slice G, R14U-07): the typed value as the "Moved to …" status quotes it -- a bare number gets its unit ("1.4838 s"),
 * a clock form ("0:01.4838") is already self-evident; a frame token never moves, so it never reaches here.
 */
export function typedTimeLabel(typed: string): string {
    const t = typed.trim().replace(/,/g, '.');
    const secs = /^(\d+(?:\.\d+)?|\.\d+)s?$/.exec(t);
    return secs ? `${secs[1]} s` : t;
}

const COPY_ONLY_VIDEOS = 'Only videos open in the Frame picker.';

export const COPY = Object.freeze({
    // ---- Header primary (§6.2 root row; the ZIP is built on /downloads, so no "to ZIP") ----
    convertOne: (format: string) => `Convert frame to ${format.toUpperCase()}`,
    convertMany: (frames: number) => `Convert ${frames} ${plural(frames, 'frame', 'frames')}`,
    convertSheet: (format: string) => `Convert grid to ${format.toUpperCase()}`,
    /** Round 9 (#13): the header's primary under 480 px, where "‹ Back" and the title need the room; the panel's summary card carries the full result line. */
    convertShort: 'Convert',

    // ---- Modes (labels come from xconfig; these are the picker's own headings) ----
    gridTitle: 'Frames you will get',
    sheetToggleVideo: 'Video',
    sheetToggleSheet: 'Sheet',
    wholeClip: 'Whole clip',
    /**
     * R14 (slice G, BUG-017/018): the zoomed strip's key is named for what it does -- one frame per tile, aimable by thumb
     * ("Near this frame" advertised no precision; on a 9:56 clip the whole-clip strip is 38 frames per pixel).
     */
    nearThisFrame: 'Fine · around this frame',
    startHere: 'Start here',
    endHere: 'End here',
    reset: 'Reset',
    /** Round 7 (UX7 #4): the clip-global key, named for what it is; "near here" is the first key. */
    jumpToSharpest: 'Sharpest in clip',
    sharpestNear: 'Sharpest near here',
    sharpestNearHint: 'The sharpest frame close to this one',
    sharpestFrame: 'Sharpest frame',
    typeATime: 'Type a time',
    looksSharp: 'Looks sharp',
    looksSoft: 'Looks soft',
    /**
     * T2 (EDITOR_REOPEN_PLAN R-F): the same verdicts over the edge's 540p `preview` (a restored clip, or a codec this browser
     * cannot play) -- the chip judges the picture under it, not the full-size frame the export takes from the original.
     */
    looksSharpInPreview: 'Looks sharp in this preview',
    looksSoftInPreview: 'Looks soft in this preview',
    sharp: 'Sharp',
    sharpnessLegend: 'Green = the sharpest moments',
    /** Batch 3 (R14U-22, for slice G's filmstrip legend): the soft half. One wording on every width since G's a3843ee6a (the short forms went). */
    sharpnessLegendSoft: 'Orange = looks soft',
    /**
     * Batch 3 (R14U-11, for slice I's interval chips): an interval said in FRAMES on a coarse clip -- "Every frame", "Every 2nd frame",
     * "Every 3rd frame" … (the 2 fps GIF's "0.5 s" is every frame; "1/60 s" there means nothing to a user).
     */
    intervalEveryFrame: 'Every frame',
    intervalEveryNth: (n: number) => (Math.floor(n) <= 1 ? COPY.intervalEveryFrame : `Every ${ordinal(n)} frame`),
    /**
     * "23 frames · every 2 s · 0:04–0:48" (§6.2 range-bar). Batch 3 (R14U-11): with the clip's `fps`, on a COARSE clip (≤ 5 fps) whose
     * interval is a whole number of frames, the interval reads in frames -- "10 frames · every frame · 0:00–0:05" on the 2 fps GIF,
     * "every 2nd frame" for 1 s there; a finer clip or a fractional interval keeps the seconds. Without `fps` nothing changes.
     */
    summaryChip: (frames: number, intervalS: number, start: number, end: number, fps?: number | null) => {
        const every = intervalInFrames(intervalS, fps);
        const interval = every === null ? `every ${seconds(intervalS)} s` : every === 1 ? 'every frame' : `every ${ordinal(every)} frame`;
        // Review 7 P2-08: whole seconds read "0:01–0:01" for 1.000–1.500; when the ends round alike, milliseconds tell them apart.
        const [a, b] = clock(start) === clock(end) ? [formatHms(start), formatHms(end)] : [clock(start), clock(end)];
        return `${frames} ${plural(frames, 'frame', 'frames')} · ${interval} · ${a}–${b}`;
    },
    /** "30 in 4 columns = 7 rows + 2" (§3.2, the ragged-row line). */
    raggedRows: (frames: number, cols: number, rows: number, remainder: number) =>
        remainder === 0
            ? `${frames} in ${cols} ${plural(cols, 'column', 'columns')} = ${rows} ${plural(rows, 'row', 'rows')}`
            : `${frames} in ${cols} ${plural(cols, 'column', 'columns')} = ${rows - 1} ${plural(rows - 1, 'row', 'rows')} + ${remainder}`,
    /** The grid cell's accessible name: "Frame 3 of 23 · 0:04". */
    frameCell: (index1: number, total: number, t: number) => `Frame ${index1} of ${total} · ${clock(t)}`,
    /** The preview chip's second numbering in sequence mode (§6.3). IT1 (copy 3): "Sequence 1/15" -- ~84 px; the 92 px chip clipped "Frame 1 of 15 in your sequence" to "Frame 1 of …". */
    /** Batch 3 (R14U-30): which of YOUR frames the picture shows in Several frames -- "Frame 3 of your 5" (was "Sequence 3/5", a word nobody else used). */
    frameInSequence: (index1: number, total: number) => `Frame ${index1} of your ${total}`,

    // ---- Preparing states (§6.2 copy.ts row; §6.6) ----
    // Round 4 copy pass (owner: "make the language very clear for an average/new user"): short
    // sentences, no "probe", "rendition", "frame source" or "sharpness score" anywhere on screen.
    detailsOnceUploaded: (ext: string) => `${ext.toUpperCase()} · details once uploaded`,
    uploadingFilmstrip: 'Uploading… the filmstrip appears in a moment.',
    /**
     * Round 11 (K, R10 N1): the upload's own fraction while the tool is not usable yet -- "Ready" used to read at 0.25 s over
     * disabled keys, an empty counter and a blank strip for as long as the upload took (8 s Chromium / 11.5 s Firefox for a
     * 16 MB MP4; 23–27 s for a 249 MB `.mov`).
     */
    uploading: (percent: number) => `Uploading \u00b7 ${Math.max(0, Math.min(100, Math.round(percent)))} %`,
    /** Review 7 P1-01: Convert was pressed before the upload finished -- the job starts by itself when it has. */
    /** WP-07: the upload failed while a Convert press waited for it -- nothing was converted. */
    convertDroppedUploadFailed: 'The upload failed, so nothing was converted \u2014 add the file again, then Convert',
    /** WP-01: a Convert press waiting (≤ 5 s) for the clip's last real frame to be read. */
    findingLastFrame: 'Finding the clip\u2019s last frame \u2014 your frame converts in a moment',
    convertWhenUploaded: (percent: number) => `Uploading \u00b7 ${Math.max(0, Math.min(100, Math.round(percent)))} % \u2014 your frames convert as soon as it\u2019s done`,
    /** IT1 (fe #7): the Files row's progressbar name. */
    uploadingName: (name: string) => `Uploading ${name}`,
    /** Round 11 (K, R10 N10): Back from /downloads -- the clip comes back from the server, nothing is uploading. */
    restoring: 'Restoring your video\u2026',
    previewReady: 'Ready · pick your frame',
    sharpnessMeasuring: 'Checking which frames are sharpest…',
    fillingIn: 'Filling in from the start.',
    noSource: 'Preview isn\u2019t available for this file yet. Go back to Quick convert and type a time instead.',
    /** Round 6 (QA#11 / SDE#2): the browser could not play the file itself and the server's preview is still being made. */
    preparingPreview: 'Preparing preview\u2026',
    /**
     * Round 9 (L3, R8 #4 -- the Studio's S2 line): the server is making the browser-playable preview of a
     * clip this browser cannot play; the number is an ESTIMATE from the file's size (`utils/preview-estimate.ts`).
     */
    convertingForPreview: (percent: number) => `Converting for preview \u00b7 ${Math.max(0, Math.min(100, Math.round(percent)))} %`,
    /** Round 5 (P4): the browser cannot play the codec, but the server's thumbnails are on the strip. */
    noPlayback: 'This browser can\u2019t play this video. Pick a time on the filmstrip below \u2014 the conversion still works.',
    thumbsFromServer: 'Thumbnails from our server \u00b7 this browser can\u2019t play this video',
    thumbsUnavailable: 'Thumbnails aren\u2019t available for this video\u2019s codec',
    noSourceAction: 'Go back to Quick convert',
    probeFailed: 'We couldn\u2019t read this video\u2019s length, so Several frames and Contact sheet are off for now. One frame still works.',
    expired: 'This video isn\u2019t on our servers any more. Add it again to pick another frame.',
    /** Review 7 P2-06: a picker link whose session this browser has no record of -- say what is true, not "gone from our servers". */
    sessionNotHere: 'This picker session isn\u2019t saved in this browser. Frames you converted from it are on Downloads; add the video again to pick more.',
    openDownloads: 'Open Downloads',
    storageDisabled: 'Your browser isn\u2019t saving your picker settings, so Back from Downloads starts fresh.',

    // ---- Big-job check (§6.5) ----
    checkTitle: 'Check before converting',
    checkFrames: 'Frames',
    checkFormat: 'Format',
    checkSize: 'Estimated size',
    checkHowLong: 'How long',
    /** §6.5: the amber size line when the estimate exceeds 50 MB. IT1 (copy 8): no "job" on a user's screen. */
    largeJob: (mbAt720p: number) => `Big download · about ${mbAt720p} MB at 720p`,
    /** §0b/§4.3: amber past 300 frames, stating the every-frame total of the clip. */
    manyFrames: (frames: number, everyFrameTotal: number) =>
        `${frames} frames is a lot. Every frame of this clip would be ${everyFrameTotal}.`,
    /** IT1 (saas #8 / copy 7): the ONE expiry number, read from the TTL -- "a few hours" was a second promise beside the Studio's 2.5 h. */
    zipNote: `Your ZIP will be on the Downloads page for ${FRAMEPICKER_TTL_HOURS} hours.`,
    back: 'Back',
    /** "How long" rows: "a few seconds" floor (§6.2 estimate.ts), else "about N seconds/minutes". */
    howLong: (s: number) => {
        if (s < 5) return 'a few seconds';
        if (s < 90) return `about ${Math.round(s / 5) * 5} seconds`;
        return `about ${Math.max(2, Math.round(s / 60))} minutes`;
    },
    /** Estimated size row: "about 12 MB" / "about <1 MB" / "about 12 MB (23 files)". */
    estimatedSize: (bytes: number, files: number) =>
        files > 1 ? `about ${megabytes(bytes)} (${files} files)` : `about ${megabytes(bytes)}`,

    // ---- §3.5 refusals mirrored for the client-side check (edge is authoritative, D-3) ----
    tooManyFrames: (frames: number) =>
        `That\u2019s ${frames} frames \u2014 more than we can make at once. Use a longer gap between frames, a shorter range, or up to ${FRAME_LIMITS.maxFramesPerJob} frames.`,
    sheetTooLarge: (frames: number) =>
        `A grid of ${frames} frames is too big for one image. Use ${FRAME_LIMITS.maxSheetCells} frames or fewer, or a narrower sheet.`,
    /** IT2 (copy N1): a count above the frames the range holds -- ffmpeg would write repeats. The field is capped there; this is for a restored record or a shrunk range. */
    moreThanClip: (frames: number, everyFrame: number) =>
        `That\u2019s ${frames} frames \u2014 more than this range has. Every frame in it would be ${everyFrame}, so ask for ${everyFrame} or fewer.`,
    rangeInvalid: 'The start is after the end. Drag the start earlier, or press Reset.',
    countTooSmall: 'For a single frame, switch to One frame. Otherwise ask for 2 frames or more.',
    /** R14 (slice C, BUG-013 follow-up): the title of an interval chip that would write no frame -- it stays on screen, inert. */
    intervalTooLong: 'Longer than your range',
    /** The §4.3 limits, stated on the check so the refusal never comes as a surprise. */
    limitsSequence: `Up to ${FRAME_LIMITS.maxFramesPerJob} frames at a time.`,
    limitsSheet: `Up to ${FRAME_LIMITS.maxSheetCells} frames in one grid, ${FRAME_LIMITS.maxSheetWidthPx} px wide at most.`,

    // ---- Progress (§6.2 progress row; §6.5) ----
    progressTitle: 'Getting your frames…',
    progressCount: (n: number, total: number) => `${n} of ${total}`,
    /** `started` false (round 14, R12 N9): none of several frames has landed yet -- never "almost done" beside "0 of N". */
    progressLeft: (s: number, started = true) => (!started && s < 5 ? 'starting…' : s < 5 ? 'almost done' : `about ${Math.round(s / 5) * 5} seconds left`),
    stageUploaded: 'Your video is uploaded',
    stageFinding: 'Saving each frame as an image',
    stageZip: 'Packing the images into a ZIP',
    progressHint: 'Long videos can take a minute — you can leave this tab open.',
    stopAndGoBack: 'Stop and go back',
    /** The polite status at 25/50/75/100 (§6.2). */
    progressAnnounce: (percent: number) => (percent >= 100 ? 'Done. Opening your downloads.' : `${percent} percent done`),

    // ---- /downloads card and entry points (§6.1, §6.5; owned by W6, worded here) ----
    downloadAllZip: (frames: number) => `Download all ${frames} ${plural(frames, 'frame', 'frames')} (ZIP)`,
    makingZip: 'Making your ZIP…',
    showDownloads: (files: number) => `Show Downloads (${files} ${plural(files, 'file', 'files')})`,
    phoneZipHint: 'On a phone the ZIP opens in the Files app.',
    pickAnotherFrame: 'Pick another frame',
    pickItOnTheVideo: 'Pick it on the video',
    pickTheFrame: 'Pick the frame',
    convertFirstFrame: 'Convert first frame',
    newPill: 'NEW',
    advertisement: 'Advertisement',

    // ---- picker-fix wave (docs/proposed/picker-fix/WAVE_BRIEF.md): pinned, every slice reads these ----
    // Files pane / empty state (mocks EmptyState.png, Main.png "Files" card)
    filesTitle: 'Files',
    filesCount: (n: number, noun: SourceNoun = 'video') => `${n} ${plural(n, noun, `${noun}s`)}`,
    addVideo: 'Add a video',
    dropAnywhere: 'or drop it anywhere on this page',
    emptyTitle: 'Pick the exact frame from your video',
    emptyBody: 'Add a video, then step through it frame by frame or save many frames at once.',
    dropOverlay: 'Drop your video to open it in the Frame picker',
    fileMeta: (duration: string, w: number, h: number, fps: number) => `${duration} · ${w} × ${h} · ${COPY.fpsLabel(fps)}`,
    /** Round 14 (K5): the same line before the edge has said the rate -- the element knows the length and the size. */
    fileMetaNoRate: (duration: string, w: number, h: number) => `${duration} · ${w} × ${h}`,
    /**
     * R14 (slice L, BUG-016): the same line for a clip whose frame rate VARIES (the edge's `frame_rate_mode: VFR`) -- there is no
     * one rate to say, so the line says that instead of an average dressed up as "20 fps".
     */
    fileMetaVariable: (duration: string, w: number, h: number) => `${duration} · ${w} × ${h} · ${COPY.variableRate}`,
    /**
     * R14 (slice L, BUG-016): the words the counter, the head and the Files row use for a clip with no fixed frame rate. Batch 3 (R14U-16):
     * "frame rate varies" -- plain words where a user reads them; "variable rate" read as a codec term.
     */
    variableRate: 'frame rate varies',
    /** Batch 3 (R14U-16): the sentence that explains it, printed on the transport's reserved hint line at Ready on such a clip (≤ 2 lines at 390). */
    variableRateExplained: 'This clip’s frame rate varies, so the picker shows times, not frame numbers.',
    /**
     * Round 13 (K, R12 N5): the frame rate as a user reads it -- "24 fps", "30 fps", "60 fps" -- never the probe's
     * "23.976025 fps" (it ran off the panel edge in the head and repeated in the header, the counter and the Files row).
     * The timeline keeps the exact rate for frame arithmetic; this is the label only.
     */
    fpsLabel: (fps: number) => `${Math.round(fps)} fps`,
    /** F1 (TODO(copy)): the tablet/phone header button that opens the Files sheet. */
    filesButton: (n: number) => `Files (${n})`,
    /** F1 (TODO(copy)): a drop or a pick that held no video at all. */
    onlyVideos: COPY_ONLY_VIDEOS,
    /** Review 7 P1-08: the refused file by name and why ("15_second_audio.mp3 is an audio file -- only videos open in the Frame picker."). */
    refusedPick: (name: string, kind: 'audio' | 'image' | 'other') =>
        `${name} ${kind === 'audio' ? 'is an audio file' : kind === 'image' ? 'is a still image' : 'isn\u2019t a video'}. ${COPY_ONLY_VIDEOS}`,
    /** F1 (TODO(copy)): the Output panel's disabled reason before a file is staged. */
    noFileYet: 'Add a video first.',
    // ---- round 3 (ROUND3_BRIEF.md R9) ----
    /** R9b: a pick or drop on a page with an accepted list held nothing from it: "Only MP4 or M4V videos open here." */
    onlyAccepted: (exts: readonly string[]) => `Only ${orList(exts.map((e) => e.toUpperCase()))} videos open here.`,
    /**
     * Round 6 (UX#19): a VIDEO in a container this page does not take -- ".mov on the MP4 page" -- names
     * the page that does: "This page takes MP4 — use Video to JPG for .mov". `target` is the page's
     * output format ("JPG"); an empty one reads "Video to image".
     */
    wrongContainer: (accepted: readonly string[], ext: string, target: string) =>
        `This page takes ${orList(accepted.map((e) => e.toUpperCase()))} \u2014 use Video to ${target ? target.toUpperCase() : 'image'} for .${ext.toLowerCase()}`,
    /**
     * Round 9 (L3, R8 #4): the picker took a video the PAGE's uploader then refused (its list is the page's
     * own): "Couldn’t add holiday.mov here — this page uploads MP4 or M4V. Use Video to JPG for .mov." Stays
     * until the next pick (never a four-second flash and then nothing).
     */
    pageRefusedContainer: (name: string, accepted: readonly string[], ext: string, target: string) =>
        `Couldn\u2019t add ${name} here \u2014 this page uploads ${orList(accepted.map((e) => e.toUpperCase()))}. Use Video to ${target ? target.toUpperCase() : 'image'} for .${ext.toLowerCase()}.`,
    /** IT1 (W3, saas #6): the link beside a refusal that names another page -- "Open Video to JPG" (the sentence said which). */
    openPageFor: (target: string) => `Open Video to ${target ? target.toUpperCase() : 'image'}`,
    /** R9b: the page's uploader refused a file after all (size, offline): "Couldn’t add clip.mkv — “.mkv” files are not supported here." */
    couldNotAdd: (name: string, reason: string) => `Couldn’t add ${name}${reason ? ` — ${reason.replace(/[.\s]+$/, '')}` : ''}.`,
    /** R9b: the page under the picker is not ready to take a file (a Back remount mid-load). */
    pageNotReady: 'The page is still loading. Try adding the video again in a moment.',
    /**
     * R14 (slice C, BUG-012): a file that carries a video extension but holds no video the browser or the edge can read -- the
     * row's status, with a Remove key; Convert waits; nothing about a timeline is invented for it.
     */
    unreadable: (name: string) => `We couldn\u2019t read ${name} \u2014 it may not be a video, or it may be damaged.`,
    /**
     * R14 (Sr review 1, MUST-FIX 2): a STILL image (a one-frame .webp / .gif / .png) staged on its own animated-image page: the
     * edge describes it (a size, no frames to step through), so it is not damaged -- there is simply nothing to pick. Names the
     * page's output ("JPG" on WebP to JPG, "PNG" on APNG to PNG); the Remove key stays beside it.
     */
    stillImage: (name: string, ext: string) => `${name} is a still image \u2014 it has one frame, so there is nothing to pick. Quick convert turns it into a${/^[AEIOU]/i.test(ext) ? 'n' : ''} ${ext.toUpperCase()}.`,
    removeFile: 'Remove',
    /**
     * R14 (slice C, BUG-003, the parent's ruling): an animated image (GIF / WebP / APNG) offered to a VIDEO page's picker is a
     * wrong-PAGE case while the switch (`picker-gate.ts#ANIMATED_IMAGE_PICKER`) is on -- "GIF files open in the Frame picker
     * on GIF to JPG." with the link "Open GIF to JPG"; with the switch off it is a can't-do-yet case that names what the
     * converter that does exist delivers: "The Frame picker can’t open clip.gif yet. GIF to JPG saves its first frame as a JPG."
     */
    animatedOpensOn: (ext: string, target: string) => `${String(ext).toUpperCase()} files open in the Frame picker on ${String(ext).toUpperCase()} to ${String(target).toUpperCase()}.`,
    openAnimatedPage: (ext: string, target: string) => `Open ${String(ext).toUpperCase()} to ${String(target).toUpperCase()}`,
    animatedNotYet: (name: string, ext: string, target: string) =>
        `The Frame picker can\u2019t open ${name} yet. ${String(ext).toUpperCase()} to ${String(target).toUpperCase()} saves its first frame as a ${String(target).toUpperCase()}.`,
    /** Header back link: "← Video to JPG converter" (the page's own title, not "Quick convert"). */
    backTo: (pageTitle: string) => `← ${pageTitle}`,
    pickerTitle: 'Frame picker',
    // Output panel presentation (mock Main.png right column)
    whatYouGet: 'What you get',
    formatLabel: 'Format',
    qualityLabel: 'Quality',
    sizeLabel: 'Size',
    sizeOriginal: 'Original',
    sizeCustom: 'Custom',
    sizeOriginalNote: (w: number, h: number, noun: SourceNoun = 'video') => `Original keeps ${COPY.yourNoun(noun)}\u2019s own size, ${w} × ${h}.`,
    /** Round 7 (UX7 #5): the custom row's aspect lock (aria-label / title), and the upscale line under it. */
    sizeLockAspect: 'Keep the shape: one number sets the other',
    sizeUnlockAspect: 'Shape unlocked: width and height on their own',
    sizeUpscaleNote: (w: number, h: number, noun: SourceNoun = 'video') => `Bigger than the ${noun} (${w} × ${h}), so the image will look soft.`,
    /**
     * Round 13b (ZZ2, R13 QA N4 -- "show feedback always"): a Size preset taller than the video is kept at the video's own size
     * (`outputDims` caps the preset at the source height); the chip lights, and this line says what actually happens.
     */
    sizePresetCappedNote: (preset: string, w: number, h: number, noun: SourceNoun = 'video') => `${preset} is larger than ${COPY.yourNoun(noun)} (${w} × ${h}) — the frame keeps its size, ${w} × ${h}.`,
    resultLabel: 'Result',
    /** Batch 3 (R14U-28): the Result value while the ask is refused -- nothing is promised, nothing is priced. */
    resultRefused: '\u2014',
    /** Batch 3 (R14U-17): the label of the file-name row under Result. */
    savesAsLabel: 'Saves as',
    resultOne: (ext: string, w: number, h: number) => `one ${ext.toUpperCase()} · ${w} × ${h}`,
    /** "23 JPGs · one ZIP"; R14 (BUG-013, parent ruling): a gap longer than the range is the ONE frame at its start -- "1 JPG · one ZIP", still a ZIP. */
    resultMany: (n: number, ext: string) => `${n} ${plural(n, ext.toUpperCase(), `${ext.toUpperCase()}s`)} · one ZIP`,
    /** R14 (BUG-013): the Result row while the estimate is genuinely 0 -- an inverted range, a length not known yet. Never "0 JPGs · one ZIP". */
    resultNoFrames: 'No frames',
    resultSheet: (ext: string, w: number, h: number) => `one ${ext.toUpperCase()} grid · ${w} × ${h}`,
    // TODO(copy) F4: the size-less forms while the probe has not answered (WAVE_BRIEF F4).
    resultOneNoSize: (ext: string) => `one ${ext.toUpperCase()}`,
    resultSheetNoSize: (ext: string) => `one ${ext.toUpperCase()} grid`,
    estimatedSizeLabel: 'Estimated size',
    about: (mb: string) => `About ${mb}`,
    moreOptions: 'More options',
    fewerOptions: 'Fewer options',
    /** Round 5 (P5): the MORE OPTIONS section card's disclosure key. */
    show: 'Show',
    hide: 'Hide',
    // Phone summary card + sheet (mock MobileOutput.png)
    optionsButton: 'Options',
    optionsSheetTitle: 'Options',
    /** F2 (TODO(copy)): the sheet's close button (`PickerSheetComponent`), aria-label only. */
    sheetClose: 'Close',
    summaryLine: (ext: string, quality: string) => `${ext.toUpperCase()} · ${quality}`,
    // TODO(copy) F4: the size part of the phone summary card ("Original 3240×2160" / "1080p" / "Custom").
    // Filmstrip / transport
    /** Round 7 (UX7 #9): the time field's hint -- sr-only normally, shown under the field while the entry is invalid. */
    timeHint: 'Type 7.267, 0:07.267 or 219f, then press Enter.',
    /**
     * R14 (slice C, BUG-028): the same hint DERIVED from the loaded clip -- its last frame's time in both forms and its frame
     * count as a frame token ("Type 7.267, 0:07.267 or 219f, then press Enter." for a 7.3 s clip at 30 fps), so the example
     * is always inside the clip. A provisional timeline (an assumed rate) teaches the two time forms only; nothing without one.
     * R14 (Sr review 1, SHOULD 8): the clock form is the field's own `formatHms` -- hours once ≥ 1 h ("1:02:03.467"), which
     * `parseTimecode` reads; a hand-rolled "62:03.467" was the hint's own example the field refused.
     */
    timeHintFor: (tl: { duration: number; fps: number; frameCount: number; provisional?: boolean; variable?: boolean } | null | undefined) => {
        if (!tl || typeof tl !== 'object' || !(tl.fps > 0) || !(tl.frameCount > 0)) return COPY.timeHint;
        // Sr review 5 NIT 7: while the rate is PROVISIONAL the last frame's time is a guess on the assumed rate (12.100 that turns into
        // 12.083 when the facts land) -- quote a mid-clip whole second instead, which the facts cannot change.
        // Sr review 6 SHOULD 2: a whole second at ≥ 2 s; under that (short GIFs are the common case) a whole tenth of the midpoint, never 0.000.
        if (tl.provisional) {
            const half = Math.max(0, tl.duration / 2);
            const mid = tl.duration >= 2 ? Math.floor(half) : Math.floor(half * 10) / 10;
            return `Type ${mid.toFixed(3)} or ${formatHms(mid)}, then press Enter.`;
        }
        const last = Math.max(0, (tl.frameCount - 1) / tl.fps);
        const secs = last.toFixed(3);
        const clockForm = formatHms(last);
        // R14 (slice L, BUG-016): a variable-rate clip has no frame grid -- the two time forms, and what the frame keys do there.
        if (tl.variable) return `Type ${secs} or ${clockForm}, then press Enter. ${COPY.variableStepNote}`;
        return `Type ${secs}, ${clockForm} or ${tl.frameCount}f, then press Enter.`;
    },
    /** R14 (slice L, BUG-016): the second sentence of the hint under a variable-rate clip. */
    variableStepNote: 'The frame keys move about one frame at a time — this clip’s frame rate varies.',
    /** R14 (slice L, BUG-016): a typed frame token (`201f`) on a clip with no frame grid -- refused, with the way in. */
    vfrTypeATime: 'This clip has a variable frame rate — type a time.',
    /** Batch 3 (R14U-31, H's NEEDS): a typed frame token before the probe has said the clip's rate -- held off, never counted on the assumed rate. */
    frameTokenNotYet: 'Frame numbers work once the clip’s frame rate is known — type a time for now.',
    /** Batch 3 (R14U-31, H's NEEDS): the status line while the edge reads the clip (Ready waits for its facts now). */
    readingClip: 'Reading the clip…',
    /** Sr review 5 SHOULD 2 (H's NEEDS): beside Saves-as when the picture is the edge's rendition (a 4K body) -- one line at 372 px. */
    renditionNote: 'Preview: our server’s copy. Saved frame: the original.',
    /** Sr review 5 NIT 15 (H's NEEDS): the fuller sentence, for the note's `title`. */
    renditionNoteLong: 'The preview comes from our server’s copy of the clip; the saved frame is taken from your original, so the picture here can differ by up to one frame.',
    /** R14 (slice L, BUG-016): the frame keys' names under a variable-rate clip (the step is the AVERAGE frame). */
    stepAboutOneFrame: (dir: 'back' | 'forward') => (dir === 'back' ? 'Back about one frame' : 'Forward about one frame'),
    stepAboutOneFrameCaption: '≈ 1 frame',
    /**
     * R14 (BUG-011): a typed time inside a frame's span is moved to that frame's START and the move is said out loud
     * (`role="status"` in the hint slot for a few seconds): "Moved to 0:01.467 · frame 45 — the frame showing at 1.4838."
     */
    movedToFrame: (hms: string, frame: number, typed: string) => `Moved to frame ${frame} (${hms}) — the frame showing at ${typedTimeLabel(typed)}.`,
    /**
     * SMALL-1 (prod fixes 2026-10-02), copy deck XS1-2: a typed or restored time beyond the video's real last frame (the edge's
     * `last_frame_time` -- the container was longer than the video) -- snapped to that frame and said, quoting the field's own time.
     */
    videoEndsAt: (hms: string) => fmtCopy(copyOf('XS1-2'), { t: hms }),
    /** Batch 3 (R14Q-34): a typed time past the end of a VARIABLE-rate clip (no frame number to name) -- moved to the clip's last moment, said. */
    movedToClipEnd: (hms: string, typed: string) => `Moved to ${hms} — the clip ends there (you typed ${typedTimeLabel(typed)}).`,
    /** R14 (slice G, BUG-017): every strip hint names the view it describes, so the toggle's meaning is one glance away. */
    filmstripHint: 'Whole clip · click anywhere on the strip, or drag.',
    /** Round 7 (UX7 #3): no "pinch to zoom in" -- nothing handled the pinch, the browser zoomed the page 3.85x. Round 8 (O13): the whole-clip strip on a phone -- tap or drag. */
    filmstripHintTouch: 'Whole clip · tap to jump about there; use Fine to land on one frame.',
    /** Round 8 (O13): the zoomed strip on a phone -- a swipe is the strip's scroll, a tap places the cursor, the knob drags. */
    filmstripHintTouchNear: 'Fine · one frame per tile — tap a frame, swipe along the clip, drag the knob.',
    /** Round 4 (clarity): what the two strip views mean, one short line each, shown for the active one. */
    filmstripHintNear: 'Fine · one frame per tile, scroll sideways for more.',
    frameOf: (n: number, total: number, fps: number) => `Frame ${n} of ${total} · ${COPY.fpsLabel(fps)}`,
    /**
     * R14 (slice L, BUG-022, R14-SRV5 naming grammar): the file name a ONE-frame job is saved as -- the SERVER names it; the
     * picker only says the shape once. A clip with a frame grid: `<stem>-f<frame, 4 digits>-<clock(at)>.<ext>`
     * ("B-oracle-30fps-8s-f0045-0_01.467.jpg", the 1-based frame and its start); a variable-rate clip (`frame` null):
     * `<stem>-<clock(t)>.<ext>` ("D-vfr-from-oracle-0_10.000.jpg").
     */
    savesAs: (stem: string, frame: number | null, at: number, ext: string) => (frame === null
        ? `${stem}-${clockName(at)}.${ext}`
        : `${stem}-f${String(Math.max(1, Math.floor(frame))).padStart(4, '0')}-${clockName(at)}.${ext}`),
    /**
     * R14 (slice L, BUG-022): the contact sheet's file name -- `<stem>-sheet-<clock(start)>-<clock(end)>.<ext>`. Batch 3 (R14Q-19 a):
     * the server ALWAYS names a picker sheet with the range (an empty wire start is 0, an empty end the clip's end -- ironman
     * `FramePickerRequest.parseRange`), so the caller passes the numbers the wire will carry; the bare `<stem>-sheet.<ext>` is kept
     * only for a caller that truly has no range (the shared lib's fallback), which a picker job never is.
     */
    sheetName: (stem: string, start: number | null, end: number | null, ext: string) => (start === null || end === null
        ? `${stem}-sheet.${ext}`
        : `${stem}-sheet-${clockName(start)}-${clockName(end)}.${ext}`),
    /**
     * Batch 3 (R14Q-20): the name a Several-frames job's ZIP is saved as -- `<stem>-frames.zip`, the shared lib's rule
     * (`FramePickerNaming.readableName`). Until ironman `c8653a59` (R14-SRV7) the edge served `<stem>.zip`, because its download route
     * found no picker context for the ZIP (`R14_BATCH3_SERVER_ASKS.md` §2); that lookup now matches the ZIP too.
     */
    zipName: (stem: string, ext: string = 'zip') => `${stem}-frames.${ext}`,
    /**
     * Batch 3 (R14Q-20): the Several-frames Result row's "Saves as" -- the members' names (`<stem>-frame-0001.<ext>` …, the server's
     * own shape) and the ZIP they come in: "B-oracle-30fps-8s-frame-0001.jpg to B-oracle-30fps-8s-frame-0005.jpg, in B-oracle-30fps-8s.zip".
     */
    savesAsSequence: (stem: string, count: number, ext: string) => {
        const n = Math.max(1, Math.floor(count));
        const member = (i: number) => `${stem}-frame-${String(i).padStart(4, '0')}.${ext}`;
        return n === 1 ? `${member(1)}, in ${COPY.zipName(stem)}` : `${member(1)} to ${member(n)}, in ${COPY.zipName(stem)}`;
    },
    /**
     * Batch 3 (R14Q-23): the Picked-frames Result row's "Saves as" -- every pin is its own ONE-frame job, named like One frame's
     * (`savesAs`). Up to three pins are listed; more are the first name and the count.
     */
    savesAsPicked: (names: readonly string[]) => (names.length === 0 ? ''
        : names.length <= 3 ? names.join(', ')
            : `${names[0]} and ${names.length - 1} more (one per pinned frame)`),
    // F5 — grid, sheet preview, shortcuts, nudge (mocks Sequence.png, ContactSheet.png, EntryTab.png)
    gridHint: 'Click one to jump to it.',
    /** Review 7 JD-05: under a grid capped at its first cells. */
    gridMore: (n: number) => `\u2026and ${n} more ${n === 1 ? 'frame' : 'frames'} in this job.`,
    /** Round 6 (QA#3 / UX#15): no longer "exactly the image" — the labels are the server's call (`sheetLabelsNote`). */
    sheetHint: 'The grid you will get. Click a cell to go to that frame.',
    /**
     * IT1 (W3 ← W4 be #8): the sheet's note said `labelsDropped=true` -- this edge's ffmpeg has no `drawtext`, so the
     * delivered sheet has no time stamps and no title. Said once on the /downloads card (`ui/file-list/frame-picker-card.ts`).
     */
    sheetLabelsDropped: 'Time stamps and the title couldn\u2019t be added this time \u2014 the frames are fine.',
    /** IT1 (copy 9): something a user can picture, instead of "depend on the server". */
    sheetLabelsNote: 'Time stamps and the title are added when the sheet is made \u2014 they may sit a little differently.',
    /** Batch 3 (R14U-26, for slice I's sheet preview): while the server cannot stamp a sheet, say so up front instead of promising and apologising. */
    sheetLabelsUnavailable: 'Time stamps and titles aren\u2019t available on this server yet.',
    /**
     * Review 7 K5: on a contact sheet the SIZE row is the frame size DERIVED from Columns and Sheet width (the edge ignores the
     * page's Size there; offering 720p that did nothing was the owner's "the panel is lying"). The edge's own arithmetic.
     */
    sheetFrameSize: (w: number, h: number) => `Each frame ${w} \u00d7 ${h} px`,
    sheetFrameWidth: (w: number) => `Each frame ${w} px wide`,
    sheetFrameSizeNote: 'Set by Columns and Sheet width.',
    /** Review 7 K5 (review 6 A9-08): a typed column count outside the range is refused, not silently turned into 1 (a 1920 × 39614 strip). */
    /** Review 7 K5: a sheet taller than its format can store ("A 107024 px tall JPG is more than JPG can hold (65535 px). Use 2 columns or more."). */
    sheetTooTall: (height: number, format: string, max: number, fitColumns: number | null) =>
        `A ${height} px tall ${format} is more than ${format} can hold (${max} px). ${fitColumns ? `Use ${fitColumns} columns or more.` : 'Use fewer frames or a narrower sheet.'}`,
    sheetColumnsRange: (min: number, max: number, kept: number) => `Columns go from ${min} to ${max} \u2014 kept ${kept}.`,
    /** Batch 3 (R14U-28, for slice I's sheet preview): the last cell when a sheet asks for more cells than one image can hold -- "…and 380 more". */
    sheetMoreCells: (n: number) => `\u2026and ${Math.max(0, Math.floor(n))} more`,
    /** Round 6: a grid slot the clip has no frame for (its end instant). */
    sheetBlankCell: 'Empty cell',
    /** Round 6 (UX#14 / SDE#10): why "Exactly 12" reads "= 11 frames" over a whole clip. */
    endNotAFrame: 'The clip\u2019s very end isn\u2019t a frame, so it isn\u2019t counted.',
    previewToggleLabel: 'Preview',
    /** Round 8 (O16 / S5): the two halves of the stage, Premiere's Source | Program -- "Input · your video" | "Output · what you'll get". */
    inputLabel: 'Input',
    inputSub: 'your video',
    outputLabel: 'Output',
    outputSub: 'what you\u2019ll get',
    /** The one-frame Output preview's caption: "JPG · 1920 × 1080". */
    outputFrameCaption: (ext: string, w: number, h: number) => `${ext.toUpperCase()} · ${w} × ${h}`,
    outputFrameCaptionNoSize: (ext: string) => ext.toUpperCase(),
    /**
     * Round 12 (N3, the owner's P4 -- "take a step back and evaluate that UI/UX again"): the Pin key lives in the
     * transport row (on the Input picture on a phone), never on the Output. Pinning keeps the frame you are on;
     * the first pin makes "Picked frames" WHAT YOU GET, and the Output shows the pinned frames only -- each with
     * its time and a remove key. Convert makes one image per pin (one download each). Up to `MAX_PINS`.
     */
    pinFrame: 'Pin this frame',
    /**
     * R14 (Sr review 1, NIT 14; slice I's NEEDS): the sequence key's label while there is no count to say -- ONE spelling for the
     * header key and the panel's (`output-panel.component.tsx#CONVERT_FRAMES_PENDING` reads the same words until it imports this).
     */
    convertFramesPending: 'Convert frames',
    /** R14 (slice C, BUG-010 c): the Pin key's title says what pinning does; its label stays "Pin this frame". */
    pinTitle: 'Pin this frame \u2014 Convert exports pinned frames until you choose One frame again',
    /**
     * R14 (slice C, BUG-010 a): the Input picture's marker while Picked frames is what you get and the playhead is not on a
     * pin -- "Showing 0:01.467 · Convert exports your 3 pinned frames", with the key "Use this frame instead" (One frame at
     * the playhead; the pins stay).
     */
    inputNotExported: (hms: string, n: number) => n === 1 ? `Showing ${hms} \u00b7 Convert exports your pinned frame` : `Showing ${hms} \u00b7 Convert exports your ${n} pinned frames`,
    /** Batch 3 (R14Q-32): the same sentence for a narrow band (≤ 40 characters) -- the root picks by width; the long form stays in `title`. */
    notExportedShort: (hms: string, n: number) => (n === 1 ? `Showing ${hms} \u00b7 pinned frame exports` : `Showing ${hms} \u00b7 pinned frames export`),
    /** Batch 3 (H's NEEDS, R14Q-32): the ONE-line band beside the 139 px key at a 372 px stage -- ≤ 30 characters, still saying what happens. */
    notExportedOneLine: (hms: string) => `Exports your pins \u00b7 ${hms}`,
    useThisFrame: 'Use this frame instead',
    /** Review 7 JC-R2-02: on the picture while there is no playable body (poster / nearest thumbnail). */
    pictureIsPlaceholder: 'Placeholder picture \u00b7 not this exact frame',
    pinNeedsPreview: 'Pin works once the preview is ready \u2014 the picture is not this exact frame yet',
    /**
     * Review 7 K8 (owner): the band is PERMANENT while Picked frames is what you get -- on a pin it says which one the picture is
     * ("Pinned frame 2 of 3 · 0:05.272", with Unpin); off a pin it keeps `inputNotExported`. Nothing appears or goes as you move.
     */
    onPinMarker: (hms: string, i: number, n: number) => `Pinned frame ${i} of ${n} \u00b7 ${hms}`,
    /** The same, ≤ 30 characters, for the narrow band (the stylesheet draws `data-short`). */
    onPinOneLine: (hms: string, i: number, n: number) => `Pin ${i} of ${n} \u00b7 ${hms}`,
    unpinThis: 'Unpin',
    /** R14 (slice C, BUG-010 b): said once (`role="status"`) when the first pin switches what you get. */
    pinnedAnnounce: 'Pinned \u00b7 Convert now exports your pinned frames',
    /** The count beside the Pin key: "Pinned · 3". */
    pinnedCount: (n: number) => `Pinned \u00b7 ${n}`,
    /** The Pin key at the cap (its title), and the note under the picked grid. */
    pinLimit: (max: number) => `Up to ${max} pinned frames`,
    pinLimitNote: (max: number) => `That\u2019s the most \u2014 ${max} frames. Remove one to pin another.`,
    /** The fourth WHAT YOU GET choice, with its count: "Picked frames · 3". */
    pickedFrames: (n: number) => `Picked frames \u00b7 ${n}`,
    /** Its one-line hint. */
    pickedHint: (n: number) => n === 1 ? 'The frame you pinned, as one image. Pin more to get one image each.' : `The ${n} frames you pinned, one image each, in one download (ZIP).`,
    /** The picked grid's title and keys. */
    pickedTitle: (n: number) => `Picked frames (${n})`,
    pickedHintGrid: 'Tap one to go to it; \u00d7 removes it.',
    goToPinned: (time: string) => `Go to ${time}`,
    removePin: (time: string) => `Remove ${time}`,
    clearAll: 'Clear all',
    /** The header primary and the Result row in Picked frames. */
    convertPicked: (n: number) => `Convert ${n} pinned ${plural(n, 'frame', 'frames')}`,
    resultPicked: (n: number, ext: string, w: number, h: number) => `${n} ${ext.toUpperCase()}${n === 1 ? '' : 's'} \u00b7 ${w} \u00d7 ${h} \u00b7 one download each`,
    resultPickedNoSize: (n: number, ext: string) => `${n} ${ext.toUpperCase()}${n === 1 ? '' : 's'} \u00b7 one download each`,
    /** The picked Output's caption: "3 × JPG · 1920 × 1080". */
    pickedCaption: (n: number, ext: string, w: number, h: number) => `${n} \u00d7 ${ext.toUpperCase()} \u00b7 ${w} \u00d7 ${h}`,
    /** The progress screen's count in Picked frames: "Frame 2 of 5". */
    pickedProgress: (n: number, total: number) => `Frame ${n} of ${total}`,
    /** Round 8 (O14): the player's zoom key -- a labelled control, not a bare "1×". */
    zoomLabel: (zoom: 1 | 2) => `Zoom ${zoom}×`,
    zoomIn: 'Zoom in to 2× (the middle of the picture)',
    zoomOut: 'Show the whole picture (1×)',
    shortcutsTitle: 'Keyboard shortcuts',
    close: 'Close',
    // ---- Round 4 (R4-P1): the Output panel's own words, and the chrome keys ----
    /** The mode segments say what the user GETS; the tree's names ("Many frames") are replaced by these. */
    modeLabel: (propertyValue: string): string | null => MODE_WORDS[propertyValue]?.label ?? null,
    /** One short hint per mode, in place of the tree's paragraph. */
    modeHint: (propertyValue: string): string | null => MODE_WORDS[propertyValue]?.hint ?? null,
    howManyFrames: 'How many frames',
    framesUnit: 'frames',
    /** The interval and count rows' derived values ("= 23 frames", "= one every 2 s"). */
    derivedFrames: (n: number) => `= ${n} ${plural(n, 'frame', 'frames')}`,
    derivedEvery: (s: string) => `= one every ${s} s`,
    /** The Output panel's disabled reasons and bound lines (were literals in the panel). */
    addVideoToConvert: 'Add a video to convert.',
    useLongerGap: 'Use a longer gap between frames, or a shorter range.',
    /** IT2 (copy N1): the Convert reason when "Exactly N" is above the range's frame total. */
    /** Batch 3 (R14Q-43, I's NEEDS): pluralised -- "Ask for 1 frame or fewer …" / "Ask for 2 frames or fewer …". */
    useFewerFrames: (everyFrame: number) => `Ask for ${everyFrame} ${plural(everyFrame, 'frame', 'frames')} or fewer \u2014 that\u2019s every frame in this range.`,
    /** Batch 3 (R14Q-43, I's NEEDS): a Contact sheet asked over a range with fewer than two frames (the root's refusal copy picks it). */
    rangeTooShortForSheet: (n: number) => `This range has only ${n} ${plural(n, 'frame', 'frames')} \u2014 a contact sheet needs at least 2.`,
    /** IT2 (copy N1): the Output panel's line for the same case (the amber line would state an impossibility). */
    overClip: (frames: number, everyFrame: number) =>
        `That\u2019s ${frames} frames \u2014 more than this range has. Every frame in it would be ${everyFrame}.`,
    rangeNeedsTwo: 'Pick a range that ends after it starts and asks for 2 frames or more.',
    frameCountPending: 'The frame count appears once we know how long the video is.',
    /** IT1 (copy 8): "job" is the server's word -- "more than we can make at once (600)"; a grid is still a grid. */
    overBound: (frames: number, noun: 'job' | 'grid', bound: number) =>
        noun === 'grid' ? `That\u2019s ${frames} frames — more than one grid can hold (${bound}).` : `That\u2019s ${frames} frames — more than we can make at once (${bound}).`,
    largeJobLine: (frames: number, everyFrame: number | null) =>
        // Review 7 P2-10: no "would be 600" when the 600 already ARE every frame of the range.
        everyFrame === null || everyFrame <= frames ? `A lot of frames · ${frames}` : `A lot of frames · ${frames} (every frame in this range would be ${everyFrame})`,
    ragged: (cells: number, cols: number, fullRows: number, remainder: number) =>
        `${cells} in ${cols} columns = ${fullRows} ${plural(fullRows, 'row', 'rows')} + ${remainder}`,
    /** IT1 (W3, ux 8/9): the phone shell's ⋯ menu (the Studio's words: `studio-more`, "Fit to screen", "About this tool ↓") and the tab bar's name. */
    more: 'More',
    fitScreen: 'Fit to screen',
    aboutTool: 'About this tool \u2193',
    phonePanesLabel: 'Frame picker panes',
    /** The header's fullscreen key and the Output column's fold key (C5; ids picker-fullscreen / picker-panel-toggle-output). */
    fullscreenEnter: 'Fullscreen',
    fullscreenExit: 'Exit fullscreen',
    /** Round 9 (L3, R8 #6): ONE name per thing -- the preview beside the video is "Output"; the settings column / sheet is "Options", as the phone already said. */
    outputHeading: 'Options',
    panelHide: (name: string) => `Hide ${name}`,
    panelShow: (name: string) => `Show ${name}`,
    /**
     * Round 12 (N3, P6 -- the Studio's two-row head): row 1 is the clip (thumbnail · name · "0:48 · 1920 × 1080 · 30 fps"),
     * row 2 the panel's name and its keys as ghost keys: the fold (chevron, "Hide Options"), Reset (the page's defaults), Shortcuts.
     */
    headReset: 'Reset',
    headResetTitle: 'Reset every option to the page\u2019s defaults',
    headShortcuts: 'Shortcuts',
    /** The shortcut sheet's rows (were literals in the sheet). */
    shortcutFrame: 'One frame back or forward',
    shortcutBigStep: (step: string) => `A bigger step (${step})`,
    shortcutEnds: 'First or last frame',
    shortcutPlay: 'Play or pause',
    shortcutRange: 'Start or end the range at this frame',
    shortcutConvert: 'Convert',
    shortcutTimeField: 'In the time box: jump to that time',
    shortcutHelp: 'Show these shortcuts',

    nudgeLead: 'Want a specific frame?',
    nudgeSub: 'Quick convert takes the very first frame. Pick the exact moment on the video, or choose a sharper one there.',
    nudgeThumbs: 'Moments in your video',
    nudgeThumbName: (label: string) => `Pick the frame at ${label}`,
    nudgeDismiss: 'Dismiss the frame picker offer',
    noThanks: 'No thanks',
    adsKeepFree: 'Ads keep XConvert free.',
    /** Round 8 (O11): the phone's sticky bottom ad's close key. */
    closeAd: 'Close ad',
    goAdFree: 'Go ad-free',

    // ---- R14 wave B, slice H (R14U-03/04/06, R14Q-05/06/11, BUG-025/026, ask 10, BUG-014) ----
    /**
     * R14 (slice H, R14U-06 / R14Q-06): what the page's files ARE. The animated-image pages (`convert-gif-to-jpg`,
     * `convert-webp-to-jpg`, `convert-apng-to-png`) opened a picker that said "video" at every site -- "Files 1 video",
     * "Add a video", "Pick the exact frame from your video", "Input · your video", "Video preview". Every one of those
     * sentences takes the noun now; the video pages read exactly as before.
     */
    sourceNounFor: (ext: string | null | undefined): SourceNoun => {
        const e = String(ext ?? '').trim().toLowerCase().replace(/^\./, '');
        return e === 'gif' ? 'GIF' : e === 'webp' || e === 'apng' || e === 'png' ? 'image' : 'video';
    },
    /** The page's noun from its slug or path (`convert-gif-to-jpg`, `/convert-webp-to-jpg?tab=picker` → GIF / image); "video" off the shape. */
    sourceNounOfSlug: (slug: string | null | undefined): SourceNoun => {
        const m = /(?:^|\/)convert-([a-z0-9]+)-to-/i.exec(String(slug ?? ''));
        return COPY.sourceNounFor(m ? m[1] : null);
    },
    /** "video" / "videos", "GIF" / "GIFs", "image" / "images". */
    nounPlural: (n: number, noun: SourceNoun = 'video') => plural(n, noun, `${noun}s`),
    /** "a video", "a GIF", "an image". */
    aNoun: (noun: SourceNoun = 'video') => `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`,
    yourNoun: (noun: SourceNoun = 'video') => `your ${noun}`,
    /** R14 (slice H, the parent's carried `countLabel`): "2 videos · 1 file couldn’t be read" -- the unreadable rows counted apart. */
    unreadableCount: (n: number) => `${n} ${plural(n, 'file', 'files')} couldn’t be read`,
    filesCountLine: (videos: number, unreadable: number, noun: SourceNoun = 'video') =>
        unreadable > 0 ? `${COPY.filesCount(videos, noun)} · ${COPY.unreadableCount(unreadable)}` : COPY.filesCount(videos, noun),
    addSource: (noun: SourceNoun = 'video') => `Add ${COPY.aNoun(noun)}`,
    emptyTitleFor: (noun: SourceNoun = 'video') => `Pick the exact frame from your ${noun}`,
    emptyBodyFor: (noun: SourceNoun = 'video') => `Add ${COPY.aNoun(noun)}, then step through it frame by frame or save many frames at once.`,
    dropOverlayFor: (noun: SourceNoun = 'video') => `Drop your ${noun} to open it in the Frame picker`,
    inputSubFor: (noun: SourceNoun = 'video') => `your ${noun}`,
    /** The player region's accessible name: "Video preview" / "GIF preview" / "Image preview". */
    previewRegionFor: (noun: SourceNoun = 'video') => `${noun === 'video' ? 'Video' : noun === 'GIF' ? 'GIF' : 'Image'} preview`,
    noFileYetFor: (noun: SourceNoun = 'video') => `Add ${COPY.aNoun(noun)} first.`,
    addToConvertFor: (noun: SourceNoun = 'video') => `Add ${COPY.aNoun(noun)} to convert.`,
    restoringFor: (noun: SourceNoun = 'video') => `Restoring your ${noun}…`,
    filesButtonFor: (n: number) => `Files (${n})`,
    /**
     * R14 (slice H, R14U-13): Quick convert's second picker entry under the upload box, named for what it does and for the page's
     * file -- "Pick the frame on your GIF" (it said "Pick it on the video" on a GIF page). The link itself stays: dropping it where
     * the tab exists is the owner's call (UX pass 2, P4).
     */
    pickItOnFor: (noun: SourceNoun = 'video') => `Pick the frame on your ${noun}`,
    /** R14 (slice H, R14U-03): the still image's Files row -- the stage says the sentence once; the row says what it is. */
    stillImageRow: 'Still image · 1 frame',
    /** R14 (slice H, R14U-03): the key beside Remove that switches to Quick convert with the file kept. */
    quickConvert: 'Quick convert',
    /** R14 (slice H, R14U-04): the collapsed Output box on a refusal -- nothing is promised. */
    outputNone: 'Nothing to show for this file.',
    /**
     * R14 (slice J, BUG-025 -- the sentence; slice H renders it; Sr review 2 SHOULD 7): the tab that detects the conflict keeps ITS
     * OWN edits unsaved -- the other tab's record stands -- so the sentence says what happens to the reader, here.
     */
    otherTab: 'This frame picker is also open in another tab — what you change here won’t be saved.',
    /** R14 (slice H, BUG-026): the Files row under a clip this session converted -- the newest job's state. */
    doneOpenDownloads: 'Done · open in Downloads',
    downloadsHref: '/downloads',
    jobPending: 'Being sent to Downloads…',
    /** Sr review 2 SHOULD 7: read for a job pressed more than a minute ago (up to the record's TTL) -- never "a moment ago". */
    jobUnknown: 'Sent to Downloads earlier · check there',
    /** R14 (slice H, BUG-026 duplicate guard): Convert pressed again for the very same frame within a minute. */
    /**
     * Batch 3 (R14U-27): the duplicate guard, said plainly and per what was asked -- the frame (ONE), the frames (Several), the sheet.
     * "Downloads" is the word the root renders as the link (`alreadyConvertedParts`).
     */
    alreadyConverted: (hms: string) => `You already converted the frame at ${hms} a moment ago \u2014 it is on Downloads.`,
    alreadyConvertedMany: (n: number, start: string, end: string) => `You already converted these ${n} frames (${start}\u2013${end}) a moment ago \u2014 they are on Downloads.`,
    alreadyConvertedSheet: (start: string, end: string) => `You already converted this contact sheet (${start}\u2013${end}) a moment ago \u2014 it is on Downloads.`,
    /** The sentence split around its link word: `[before, 'Downloads', after]`; `[sentence, '', '']` when the word is absent. */
    alreadyConvertedParts: (sentence: string): [string, string, string] => {
        const i = sentence.lastIndexOf(COPY.downloadsWord);
        return i < 0 ? [sentence, '', ''] : [sentence.slice(0, i), COPY.downloadsWord, sentence.slice(i + COPY.downloadsWord.length)];
    },
    downloadsWord: 'Downloads',
    /** Sr review 6 NIT 5 (H's NEEDS): the guard notice's link as its own element beside the sentence -- a verb, not the sentence's last word again. */
    downloadsLinkLabel: 'Open Downloads',
    convertAgain: 'Convert it again',
    /** R14 (slice H, R14Q-05): Pin on a frame that is already pinned while One frame is what you get -- back to Picked frames, said out loud. */
    pickedAgainAnnounce: (n: number) => n === 1 ? 'Picked frames again · Convert exports your pinned frame' : `Picked frames again · Convert exports your ${n} pinned frames`,
    /** R14 (slice H, R14Q-05): Pin on a frame already pinned while Picked frames IS what you get: nothing changes, and that is said. */
    alreadyPinned: 'This frame is already pinned.',
    /** R14 (slice H, BUG-024 / J's `missingStagedRefs.expired`): a staged clip the record could not bring back. */
    stagedExpired: (names: readonly string[]) => names.length === 1
        ? `${names[0]} isn’t on our servers any more — add it again to pick from it.`
        : `${orList(names)} aren’t on our servers any more — add them again to pick from them.`,
    /** R14 (slice H, BUG-014 client rule ii): a seek that takes longer than a second to present -- the status says so, never "Ready" over the old picture. */
    decodingFrame: 'Decoding this frame…',
    /**
     * R14 (Sr review 2b, MUST-FIX 27): this browser's player cannot say which frame it is showing (it echoes the time it was asked for),
     * and a variable-rate clip has no frame grid to fall back on -- the saved frame can be the one after the one shown. Said once beside
     * the file name, never promised away.
     */
    vfrEchoNote: 'In this browser the saved frame can be one later than shown for variable-rate clips.',
});

export type FramePickerCopy = typeof COPY;
